if (!customElements.get('price-slider')) {
  class PriceSlider {
    constructor(container) {
      this.container = container;
      this.min = parseFloat(container.dataset.min);
      this.max = parseFloat(container.dataset.max);
      this.currentMin = parseFloat(container.dataset.currentMin);
      this.currentMax = parseFloat(container.dataset.currentMax);
      this.step = 5;
      
      this.minInput = document.getElementById(container.dataset.minInput);
      this.maxInput = document.getElementById(container.dataset.maxInput);
      
      this.track = container.querySelector('.price-slider__track');
      this.range = container.querySelector('.price-slider__range');
      this.minHandle = container.querySelector('.price-slider__handle--min');
      this.maxHandle = container.querySelector('.price-slider__handle--max');
      this.minLabel = container.querySelector('.price-slider__label--min');
      this.maxLabel = container.querySelector('.price-slider__label--max');
      this.currency = this.minLabel ? this.minLabel.dataset.currency : '';
      
      this.isDragging = false;
      this.activeHandle = null;
      
      this.init();
    }
    
    init() {
      this.buildHistogram();
      this.updateUI();
      
      // Mouse events
      this.minHandle.addEventListener('mousedown', (e) => this.startDrag(e, 'min'));
      this.maxHandle.addEventListener('mousedown', (e) => this.startDrag(e, 'max'));
      document.addEventListener('mousemove', (e) => this.onDrag(e));
      document.addEventListener('mouseup', () => this.stopDrag());
      
      // Touch events
      this.minHandle.addEventListener('touchstart', (e) => this.startDrag(e, 'min'));
      this.maxHandle.addEventListener('touchstart', (e) => this.startDrag(e, 'max'));
      document.addEventListener('touchmove', (e) => this.onDrag(e));
      document.addEventListener('touchend', () => this.stopDrag());
      
      // Click on track to jump
      this.track.addEventListener('click', (e) => this.onTrackClick(e));
    }
    
    buildHistogram() {
      // Get histogram data from data attribute
      const histogramData = this.container.dataset.histogram;
      if (!histogramData) return;
      
      const counts = histogramData.split(',').map(n => parseInt(n));
      const maxCount = Math.max(...counts, 1);
      
      const bars = this.container.querySelectorAll('.price-slider__bar');
      bars.forEach((bar, index) => {
        const count = counts[index] || 0;
        // Scale height: minimum 5% if there are products, 0% if none
        const height = count > 0 ? Math.max(5, (count / maxCount) * 100) : 0;
        
        const fill = bar.querySelector('.price-slider__bar-fill');
        fill.style.height = `${height}%`;
      });
    }
    
    updateHistogramBars() {
      const bars = this.container.querySelectorAll('.price-slider__bar');
      const bucketSize = (this.max - this.min) / 20;
      
      bars.forEach((bar, index) => {
        const bucketMin = this.min + (index * bucketSize);
        const bucketMax = bucketMin + bucketSize;
        
        // Check if this bucket overlaps with selected range
        const isActive = bucketMax >= this.currentMin && bucketMin <= this.currentMax;
        bar.classList.toggle('active', isActive);
      });
    }
    
    startDrag(e, handle) {
      e.preventDefault();
      this.isDragging = true;
      this.activeHandle = handle;
      this.container.classList.add('dragging');
    }
    
    stopDrag() {
      this.isDragging = false;
      this.activeHandle = null;
      this.container.classList.remove('dragging');
    }
    
    onDrag(e) {
      if (!this.isDragging) return;
      
      const clientX = e.type.includes('touch') ? e.touches[0].clientX : e.clientX;
      const rect = this.track.getBoundingClientRect();
      const percent = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
      const rawValue = this.min + (percent * (this.max - this.min));
      const value = this.snapToStep(rawValue);
      
      if (this.activeHandle === 'min') {
        // Don't let min go above max
        this.currentMin = Math.min(value, this.currentMax);
      } else {
        // Don't let max go below min
        this.currentMax = Math.max(value, this.currentMin);
      }
      
      this.updateUI();
      this.updateInputs();
    }
    
    onTrackClick(e) {
      if (e.target !== this.track && e.target !== this.range) return;
      
      const rect = this.track.getBoundingClientRect();
      const percent = (e.clientX - rect.left) / rect.width;
      const value = this.snapToStep(this.min + (percent * (this.max - this.min)));
      
      // Determine which handle is closer
      const distToMin = Math.abs(value - this.currentMin);
      const distToMax = Math.abs(value - this.currentMax);
      
      if (distToMin < distToMax) {
        this.currentMin = Math.min(value, this.currentMax);
      } else {
        this.currentMax = Math.max(value, this.currentMin);
      }
      
      this.updateUI();
      this.updateInputs();
    }
    
    snapToStep(value) {
      return Math.round(value / this.step) * this.step;
    }
    
    updateUI() {
      const minPercent = ((this.currentMin - this.min) / (this.max - this.min)) * 100;
      const maxPercent = ((this.currentMax - this.min) / (this.max - this.min)) * 100;
      
      this.minHandle.style.left = `${minPercent}%`;
      this.maxHandle.style.left = `${maxPercent}%`;
      this.range.style.left = `${minPercent}%`;
      this.range.style.width = `${maxPercent - minPercent}%`;
      
      // Update price labels
      if (this.minLabel) {
        this.minLabel.textContent = `${this.currency}${Math.round(this.currentMin)}`;
      }
      if (this.maxLabel) {
        this.maxLabel.textContent = `${this.currency}${Math.round(this.currentMax)}`;
      }
      
      this.updateHistogramBars();
    }
    
    updateInputs() {
      // Convert back to cents for Shopify
      const minValue = Math.round(this.currentMin * 100);
      const maxValue = Math.round(this.currentMax * 100);
      
      // Update hidden inputs
      this.minInput.value = minValue / 100;
      this.maxInput.value = maxValue / 100;
      
      // Trigger input event to update facets (form listens to 'input', not 'change')
      this.minInput.dispatchEvent(new Event('input', { bubbles: true }));
      this.maxInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }
  
  // Store slider instances to prevent duplicates
  const sliderInstances = new WeakMap();
  
  // Global initialization function
  window.initPriceSliders = function() {
    document.querySelectorAll('.price-slider').forEach(slider => {
      // Only initialize if not already initialized
      if (!sliderInstances.has(slider)) {
        const instance = new PriceSlider(slider);
        sliderInstances.set(slider, instance);
      }
    });
  };
  
  // Initialize on page load
  document.addEventListener('DOMContentLoaded', () => {
    window.initPriceSliders();
  });
}
